import { useEffect, useState } from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import KLayerNavigation from '../components/KLayerNavigation'
import {
  apiRequest,
  isPiiEncryptionEnabled,
  isUserLoggedIn,
  setAccessToken,
  showKAlert,
} from '../helpers'
import { t } from '../locale'
import { Company, User } from '../react-app-env'
import { fetchTeamOwner, fetchUser } from '../service/user'
import { useNavigate } from 'react-router-dom'
import KConfirm from '../components/KConfirm'
import KButton from '../components/KButton'
import EmailBrandingSection from './EmailBrandingSection'
import { fetchAccess, fetchUserEmail, validatePinCode } from '../service/client'
import KAccess from '../components/KAccess'

export default function Settings() {
  const navigate = useNavigate()
  const [user, setUser] = useState({} as User)
  const [company, setCompany] = useState({} as Company)

  const [confirmShow, setConfirmShow] = useState(false)
  const [resetConfirmShow, setResetConfirmShow] = useState(false)

  const [creatingTeam, setCreatingTeam] = useState(false)
  const [isDsSignTeamCreated, setIsDsSignTeamCreated] = useState(false)

  const [pin, setPin] = useState('')
  const [access, setAccess] = useState(false)
  const [pinRequired, setPinRequired] = useState(true)
  const [pendingAction, setPendingAction] = useState<'create' | 'delete'>('create')

  const [teamOwner, setTeamOwner] = useState('')

  useEffect(() => {
    getUser()

    fetch(`${process.env.REACT_APP_API_URL}/company-admin`)
      .then(r => r.json())
      .then(r => {
        if (!!r.company) {
          setCompany(r.company)
        }
      })
  }, [])

  useEffect(() => {
    getTeamOwner()
  }, [])

  const getTeamOwner = async () => {
    const data = await fetchTeamOwner()
    setTeamOwner(data?.owner ? data.owner.firstName + ' ' + data.owner.lastName : '')
  }

  const getUser = async () => {
    const u = await fetchUser()
    setUser(u)

    if (!!u.dsSignUser && !u.dsSignUser.deleted) {
      setIsDsSignTeamCreated(true)
    }
  }

  const getAccess = async () => {
    const userResponse = await fetchUserEmail()

    let email

    if (userResponse?.user?.email) {
      email = userResponse.user.email
    }

    const result = await fetchAccess(email)

    if (!!result.accessToken) {
      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      setAccess(true)
    }
  }

  const checkPin = async (pinCode: number) => {
    try {
      const res = await validatePinCode(pinCode)

      if (res?.userAccess) {
        setAccess(false)

        if (pendingAction === 'delete') {
          handleDeleteDsSignTeam()
        } else {
          handleCreateDsSignTeam()
        }
      }
    } catch (err) {
      console.error('Error validating pin code:', err)
    }
  }

  function handleCreateDsSignTeam() {
    setCreatingTeam(true)

    apiRequest(`${process.env.REACT_APP_API_URL}/sign/team/`, 'GET')
      .then(r => {
        setCreatingTeam(false)

        if (r.message) {
          showKAlert(r.message.variant, r.message.content)

          if (r.message.variant === 'success') {
            setIsDsSignTeamCreated(true)
          }
        }
      })
      .catch(() => {
        setCreatingTeam(false)
        showKAlert('danger', 'serverError')
      })
  }

  function handleDeleteDsSignTeam() {
    setCreatingTeam(true)

    apiRequest(`${process.env.REACT_APP_API_URL}/sign/team`, 'DELETE')
      .then(r => {
        setCreatingTeam(false)

        if (r.message) {
          showKAlert(r.message.variant, r.message.content)

          if (r.message.variant === 'success') {
            setIsDsSignTeamCreated(false)
          }
        }
      })
      .catch(() => {
        setCreatingTeam(false)
        showKAlert('danger', 'serverError')
      })
  }

  const getInitials = (name: string) => {
    if (!name) return '?'
    return name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2) // Maksimum 2 slova
  }

  function resetData() {
    setResetConfirmShow(false)

    fetch(`${process.env.REACT_APP_API_URL}/company-admin/reset`, {
      method: 'DELETE',
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          window.location.reload()
        }
      })
  }

  function deleteCompany() {
    setConfirmShow(false)

    fetch(`${process.env.REACT_APP_API_URL}/company-admin`, {
      method: 'DELETE',
    })
      .then(r => r.json())
      .then(r => {
        if (r.ok) {
          navigate('/user/logout')
        }
      })
  }
  return (
    <>
      <Container>
        <Row>
          <div className='sidebar col-md-3 col-lg-2 p-0'>
            <KLayerNavigation logged={isUserLoggedIn()} />
          </div>
          <main className='ms-sm-auto col-lg-10 px-md-4 py-md-4'>
            <div className='col-12'>
              <p className='text-draft lead fw-bold link-offset-3 link-underline-primary'>
                {t('teamSettings')}
              </p>
            </div>

            <div className='col-12'>
              <div className='my-5 md:grid md:grid-cols-3 md:gap-6'>
                <Row>
                  <Col lg={4}>
                    <div className='d-flex justify-between section-title-blade'>
                      <div className='pe-4 sm:px-0'>
                        <p className='mt-1 text-draft'>{t(`teamInfo`)}</p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                      <div className='grid'>
                        <Row>
                          <Col>
                            <div className='d-flex justify-content-between align-items-center'>
                              <label className='fw-medium smaller text-primary required'>
                                {t('teamOwner')}
                              </label>
                              <div className='text-end'>
                                <label className='fw-medium smaller text-primary'>
                                  {t('teamName')}
                                </label>
                                <div className='fw-semibold text-draft'>{company.name}</div>
                              </div>
                            </div>
                            <Row>
                              <Col className='col-sm-1'>
                                <div className='avatar-container mt-2'>
                                  {teamOwner ? (
                                    <div className='avatar-circle'>
                                      <span className='initials'>{getInitials(teamOwner)}</span>
                                    </div>
                                  ) : null}
                                </div>
                              </Col>
                              <Col>
                                <div className='text-draft ms-4 mt-4'>{user.email}</div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row className='mt-4'>
                          <Col className='col-span-12'>
                            <h6 className='text-muted'>Team Plan Info</h6>
                            <p className='text-muted mb-1'>{t('teamAdmin')}</p>
                            <ul className='text-muted mb-1 bullet-list'>
                              <li>{t('manageBilling')}</li>
                              <li>{t('trackActivity')}</li>
                              <li>{t('controlAccess')}</li>
                            </ul>
                            <p className='text-muted mb-0'>{t('idealFormTeams')}</p>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Col>
                </Row>

                {!isPiiEncryptionEnabled() && !isDsSignTeamCreated && (
                  <Row className='mt-4'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <h6 className='text-draft fw-bold'>DS Sign Team</h6>
                          <p className='mt-1 text-draft'>{t('createDsSignTeam')}</p>
                        </div>
                      </div>
                    </Col>

                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        <p className='text-draft mb-3'>{t('createDsSignTeamDescription')}</p>

                        <div className='text-end'>
                          <Button
                            variant='primary'
                            className='text-white'
                            onClick={() => {
                              setPendingAction('create')
                              getAccess()
                            }}
                            disabled={creatingTeam}
                          >
                            {creatingTeam ? t('fetching') : t('createTeam')} »
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {!isPiiEncryptionEnabled() && isDsSignTeamCreated && (
                  <Row className='mt-4'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <h6 className='text-draft fw-bold'>DS Sign Team</h6>
                          <p className='mt-1 text-draft'>{t('deleteDsSignTeam')}</p>
                        </div>
                      </div>
                    </Col>

                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        <p className='text-draft mb-3'>{t('deleteDsSignTeamDescription')}</p>

                        <div className='text-end'>
                          <Button
                            variant='danger'
                            className='text-white'
                            onClick={() => {
                              setPendingAction('delete')
                              getAccess()
                            }}
                            disabled={creatingTeam}
                          >
                            {creatingTeam ? t('fetching') : t('deleteTeam')} »
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {!isPiiEncryptionEnabled() && <EmailBrandingSection />}

                {company.isDemo && (
                  <Row className='mt-4'>
                    <Col lg={4}>
                      <div className='d-flex justify-between section-title-blade'>
                        <div className='pe-4 sm:px-0'>
                          <p className='mt-1 text-draft'>{t('resetData')}</p>
                        </div>
                      </div>
                    </Col>
                    <Col lg={8}>
                      <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                        <p className='mt-2 mb-0 text-draft'>{t('resetDataWarning')}</p>
                        <br />
                        <KButton
                          id='btn-reset'
                          icon='arrow-counterclockwise'
                          label={t('resetData')}
                          variant='warning'
                          onClick={() => setResetConfirmShow(true)}
                        />
                      </div>
                    </Col>
                  </Row>
                )}

                <Row className='mt-4'>
                  <Col lg={4}>
                    <div className='d-flex justify-between section-title-blade'>
                      <div className='pe-4 sm:px-0'>
                        <p className='mt-1 text-draft'>{t('deleteCompany')}</p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                      <p className=' mt-2 mb-0 text-draft'>{t('companyDeleteWarning')}</p>
                      <br />
                      <KButton
                        id='btn-delete'
                        icon='trash'
                        label={t('delete')}
                        variant='danger'
                        onClick={() => setConfirmShow(true)}
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </main>
        </Row>
      </Container>

      <KConfirm
        show={confirmShow}
        onConfirm={() => deleteCompany()}
        onCancel={() => setConfirmShow(false)}
      />

      {company.isDemo && (
        <KConfirm
          show={resetConfirmShow}
          message={t('resetDataWarning')}
          onConfirm={() => resetData()}
          onCancel={() => setResetConfirmShow(false)}
        />
      )}

      {access && (
        <KAccess
          show={access}
          title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
          pin={pin}
          pinRequired={pinRequired}
          onConfirm={(pin: any) => {
            checkPin(pin)
          }}
          onClose={() => setAccess(false)}
        />
      )}
    </>
  )
}
